<script setup>
  import { computed, inject } from 'vue'
  import BcstModuleProgressHeader from './BcstModuleProgressHeader.vue'
  import checkGreenIcon from '@/assets/images/bcst-check-green.svg'
  import checkGrayIcon from '@/assets/images/bcst-check-gray.svg'
  import lockQuizzIcon from '@/assets/images/bcst-lock-quizz.svg'

  // Enriched lessons come from store.categoryDetail(moduleId).lessons.
  const props = defineProps({
    moduleTitle: { type: String, default: '' },
    lessons: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    completedCount: { type: Number, default: 0 },
    percent: { type: Number, default: 0 },
    currentLessonId: { type: String, default: null },
  })

  const customNavigation = inject('customNavigation')

  // A lesson counts as completed only when truly finished: a video watched, or a quiz
  // PASSED. A quiz that is Not Passed (failed) — or in any other non-terminal state —
  // must not appear under "Completed"; it stays in the pending group. Matches the
  // store's completion definition (['watched','passed']).
  const DONE_STATUSES = ['watched', 'passed']
  function isDone(lesson) {
    return DONE_STATUSES.includes(lesson.status)
  }

  const currentLesson = computed(
    () => props.lessons.find(l => l.id === props.currentLessonId) ?? null
  )
  
  // Check if current lesson is completed
  const isCurrentLessonCompleted = computed(
    () => currentLesson.value ? isDone(currentLesson.value) : false
  )
  
  // Completed lessons include ALL done lessons (even if one is current)
  const completedLessons = computed(
    () => props.lessons.filter(l => isDone(l))
  )
  
  const notStartedLessons = computed(
    () => props.lessons.filter(l => l.id !== props.currentLessonId && !isDone(l))
  )

  // Per-type presentation number (videos and quizzes numbered independently), taken
  // from the store's enriched displayNumber so it matches every other view.
  const lessonIndexMap = computed(() => {
    const map = {}
    props.lessons.forEach((lesson) => {
      map[lesson.id] = lesson.displayNumber ?? null
    })
    return map
  })

  function isLocked(lesson) {
    return lesson.lessonType === 'quiz' && lesson.quiz?.locked === true
  }

  function lessonMeta(lesson) {
    if (lesson.lessonType === 'video') {
      return lesson.durationLabel ? `Video Lesson, ${lesson.durationLabel}` : 'Video Lesson'
    }
    if (lesson.lessonType === 'quiz') {
      const q = lesson.quiz?.questions ?? 0
      return `Quiz, ${q} Question${q !== 1 ? 's' : ''}`
    }
    return ''
  }

  function requiredLessonDisplayIndex(lesson) {
    if (!isLocked(lesson)) return null
    const reqId = lesson.quiz?.requiredLessonId
    return reqId ? (lessonIndexMap.value[reqId] ?? null) : null
  }

  function navigateToLesson(lessonId, lessonTitle) {
    if (!lessonId) return
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformLessonDetail.name,
      params: {
        lessonId,
        lessonTitle: lessonTitle ?? '',
        moduleId: currentLesson.value?.moduleId ?? props.lessons[0]?.moduleId,
        moduleTitle: props.moduleTitle,
      }
    })
  }

  function goToLesson(lesson) {
    if (!lesson?.id || lesson.id === props.currentLessonId) return
    if (isLocked(lesson)) return
    navigateToLesson(lesson.id, lesson.title)
  }

  function goToRequiredLesson(lesson) {
    const reqId = lesson.quiz?.requiredLessonId
    if (!reqId) return
    navigateToLesson(reqId, null)
  }
</script>

<template>
  <div class="tls-panel">
    <!-- Category header + progress -->
    <BcstModuleProgressHeader
      :title="moduleTitle"
      :total="total"
      :completedCount="completedCount"
      :percent="percent" />

    <!-- CURRENT -->
    <template v-if="currentLesson">
      <p class="tls-group-heading">CURRENT</p>
      <button type="button" class="tls-item tls-item--current" @click="goToLesson(currentLesson)">
        <span class="tls-item-status">
          <img
            :src="isCurrentLessonCompleted ? checkGreenIcon : checkGrayIcon"
            alt=""
            :class="[
              'tls-item-icon',
              isCurrentLessonCompleted ? 'tls-item-icon--done' : 'tls-item-icon--current'
            ]" />
        </span>
        <span class="tls-item-body">
          <span class="tls-item-title">{{ currentLesson.title }}</span>
          <span class="tls-item-meta">{{ lessonMeta(currentLesson) }}</span>
        </span>
      </button>
    </template>

    <!-- NOT STARTED -->
    <template v-if="notStartedLessons.length">
      <p class="tls-group-heading">NOT STARTED ({{ notStartedLessons.length }})</p>
      <template v-for="lesson in notStartedLessons" :key="lesson.id">
        <!-- Locked quiz: rendered as a non-interactive container (a <button> may not
             contain another interactive control, and a disabled button swallows clicks
             to its children — which broke the "Lesson N" unlock link). The only action
             is that link, which is a real <button>. -->
        <div
          v-if="isLocked(lesson)"
          class="tls-item tls-item--locked"
          aria-disabled="true">
          <span class="tls-item-status">
            <img
              :src="lockQuizzIcon"
              alt=""
              class="tls-item-icon tls-item-icon--locked" />
          </span>
          <span class="tls-item-body">
            <span class="tls-item-title">{{ lesson.title }}</span>
            <span class="tls-item-meta">{{ lessonMeta(lesson) }}</span>
            <span class="tls-item-locked-text">
              Complete
              <button
                type="button"
                class="tls-item-locked-link"
                @click.stop="goToRequiredLesson(lesson)">
                Lesson {{ requiredLessonDisplayIndex(lesson) }}
              </button>
              to unlock quiz.
            </span>
          </span>
        </div>
        <button
          v-else
          type="button"
          class="tls-item"
          @click="goToLesson(lesson)">
          <span class="tls-item-status">
            <img
              :src="checkGrayIcon"
              alt=""
              class="tls-item-icon tls-item-icon--not-started" />
          </span>
          <span class="tls-item-body">
            <span class="tls-item-title">{{ lesson.title }}</span>
            <span class="tls-item-meta">{{ lessonMeta(lesson) }}</span>
          </span>
        </button>
      </template>
    </template>

    <!-- COMPLETED -->
    <template v-if="completedLessons.length">
      <p class="tls-group-heading">COMPLETED ({{ completedLessons.length }})</p>
      <button
        v-for="lesson in completedLessons"
        :key="lesson.id"
        type="button"
        class="tls-item"
        @click="goToLesson(lesson)">
        <span class="tls-item-status">
          <img
            :src="checkGreenIcon"
            alt=""
            class="tls-item-icon tls-item-icon--done" />
        </span>
        <span class="tls-item-body">
          <span class="tls-item-title">{{ lesson.title }}</span>
          <span class="tls-item-meta">{{ lessonMeta(lesson) }}</span>
        </span>
      </button>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .tls-panel {
    border: 1px solid var(--bs-gray-300);
    box-shadow: -5px 0 8px 0 rgba(0, 0, 0, 0.1);
    background-color: var(--bs-white);
    padding: 20px;
  }

  .tls-group-heading {
    background-color: #f4f4f5;
    color: var(--bs-body-color);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.28px;
    padding: 13px 20px;
    margin: 0 -20px 8px;
  }

  /* Lesson row: reset button styling into a clickable list item */
  .tls-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 7px 0;
    cursor: pointer;

    &:hover .tls-item-title {
      text-decoration: underline;
    }
  }

  .tls-item--current {
    cursor: default;

    &:hover .tls-item-title {
      text-decoration: none;
    }
  }

  .tls-item-status {
    flex-shrink: 0;
    line-height: 1;
    padding-top: 2px;
  }

  /* SVG icons: check-green for done, check-gray for not-started, lock for locked */
  .tls-item-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  /* Lock icon is slightly larger to match its optical weight */
  .tls-item-icon--locked {
    width: 23px;
    height: 23px;
  }

  .tls-item-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .tls-item-title {
    color: var(--bs-primary);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.28px;
    line-height: 1.3;
  }

  .tls-item--current .tls-item-title {
    color: var(--bs-body-color);
  }

  .tls-item--locked .tls-item-title {
    color: var(--bs-gray-600, #6C757D);
  }

  .tls-item--locked .tls-item-meta {
    color: var(--bs-gray-600, #6C757D);
  }

  .tls-item--locked {
    cursor: default;

    &:hover .tls-item-title {
      text-decoration: none;
    }
  }

  .tls-item-meta {
    color: var(--bs-body-color);
    font-size: 14px;
    letter-spacing: -0.28px;
    line-height: 1.3;
  }

  .tls-item-locked-text {
    color: var(--bs-body-color, #212529);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.28px;
    line-height: 1.3;
  }

  .tls-item-locked-link {
    display: inline;
    background: none;
    border: none;
    padding: 0;
    color: var(--bs-primary, #5C0CB8);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.28px;
    line-height: 1.3;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:focus,
    &:focus-visible {
      outline: 2px solid var(--bs-primary);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
</style>
